import { getIsStatus } from '@/Components/posts/status';
import { CookieHandler } from '@/Telemetry/cookie-init';
import { type Hub, PostStatusEnum } from '@/graphql/types';
import type { AppStore } from '@/inertia/AppStore';
import { useEffect, useRef } from 'react';
import { chatbotInstance } from './chatbot';
import { chatbotStateManager } from './chatbotStateManager';
import { conversationHistoryService } from './conversationHistoryService';
import type { CustomGreetingPayload } from './customElements/greeting';
import type { DFMessenger } from './messenger.types';

type Props = {
  pageType: AppStore['pageType'];
  znglist?: AppStore['znglist'];
  hub?: AppStore['hub'];
};

export const __useDfMessenger__ = (props: Props) => {
  const df = useRef<DFMessenger>(document.querySelector('df-messenger'));

  const initPageType = useRef(props.pageType);
  const initId = useRef(props.znglist?.id || props.hub?.id);
  const isPrivateContent = useRef(false);

  // Check if current content is private/hidden
  const checkIfPrivateContent = () => {
    if (props.pageType === 'znglist' && props.znglist) {
      return getIsStatus(props.znglist.status, [
        PostStatusEnum.Private,
        PostStatusEnum.Hidden,
        PostStatusEnum.HiddenBS,
      ]);
    }
    if (props.pageType === 'hub' && props.hub) {
      return getIsStatus(props.hub.status, [PostStatusEnum.Private, PostStatusEnum.Hidden, PostStatusEnum.HiddenBS]);
    }
    return false;
  };

  // Generate a unique session ID for private content
  const generateSessionId = (pageType: string, contentId?: string) => {
    return `${pageType}-${contentId || 'default'}-${Date.now()}`;
  };

  // Check if we need a new session
  const needsNewSession = () => {
    const currentIsPrivate = checkIfPrivateContent();
    const currentId = props.znglist?.id || props.hub?.id;
    return chatbotStateManager.shouldStartNewSession(props.pageType as string, currentId || null, currentIsPrivate);
  };

  const getSessionParameters = async (): Promise<Record<string, unknown>> => {
    // Get conversation context parameters from both local and BigQuery
    const conversationContext = chatbotStateManager.getConversationContext();
    const userIdentifier = chatbotStateManager.getUserIdentifier();
    const localHistory = await chatbotStateManager.loadConversationHistory();

    // Get BigQuery conversation history for enhanced parameters
    const enhancedParams = await conversationHistoryService.generateSessionParameters();
    const conversationContextString = await conversationHistoryService.getConversationContextString();
    const hasHistory = await conversationHistoryService.hasConversationHistory();

    return {
      // User identification
      user_identifier: userIdentifier,

      // BigQuery conversation history parameters
      conversation_history: enhancedParams.conversationHistory || null,
      previous_topics: enhancedParams.previousTopics || null,
      last_interaction: enhancedParams.lastInteraction || null,
      session_context: enhancedParams.sessionContext || null,
      conversation_context: conversationContextString,
      has_conversation_history: hasHistory,

      // Determine user type - use BigQuery enhanced or fallback to identifier check
      user_type: enhancedParams.userType || (userIdentifier.startsWith('anonymous-') ? 'anonymous' : 'authenticated'),

      // Legacy local context (fallback)
      is_returning_user: !!(localHistory?.isActive && localHistory.messageCount && localHistory.messageCount > 0),
      last_conversation_summary: localHistory?.lastMessageSummary || null,
      message_count: localHistory?.messageCount || 0,
      last_activity: localHistory?.lastActivityAt || null,
      last_intent: conversationContext?.lastIntent || null,
      last_topic: conversationContext?.lastTopic || null,
      topics_discussed: conversationContext?.topics ? conversationContext.topics.join(',') : null,

      // Session metadata
      page_context: window.location.pathname,
      session_start_time: new Date().toISOString(),
    };
  };

  // Check if greeting should be shown
  const shouldShowGreeting = () => {
    // Check if greeting already exists in DOM
    if (chatbotInstance.hasGreetingInDOM()) {
      return false;
    }

    // If we need a new session, we should show greeting
    if (needsNewSession()) {
      return true;
    }

    // Check if we're changing page types or content - should show new greeting
    const currentPageType = props.pageType;
    const currentId = props.znglist?.id || props.hub?.id;
    const state = chatbotStateManager.getCurrentState();

    // If page type changed significantly or content changed, show new greeting
    if (
      state &&
      (state.lastPageType !== currentPageType ||
        (currentPageType !== 'front_page' && state.lastContentId !== currentId))
    ) {
      // Reset the greeting flag so we can show a new one
      chatbotInstance.resetGreeting();
      return true;
    }

    // Otherwise, only show if we haven't shown it yet
    return !chatbotInstance.hasGreetingBeenShown();
  };

  useEffect(() => {
    if (!df.current) {
      return;
    }

    const currentIsPrivate = checkIfPrivateContent();
    const currentId = props.znglist?.id || props.hub?.id;

    // Initialize args with updated default filter
    const args = {
      title: 'Zngly',
      message: 'How can I assist you today?',
      filter: [
        {
          dataStores: [
            `projects/zngly-corporate/locations/global/collections/default_collection/dataStores/${window.znRuntime?.dfDsId || ''}`,
          ],
          // Include hiddenbs in default filter
          filter: `post_status: ANY("publish", "hidden_b_s")`,
        },
      ],
    };

    // Set session parameters after waiting for Google Analytics
    const updateSessionParams = async () => {
      try {
        // Wait for Google Analytics to initialize and set the _ga cookie
        const gaReady = await CookieHandler.waitForGACookie(5000);

        const sessionParams = await getSessionParameters();

        // Check if df.current is still valid and has the method
        if (df.current && typeof df.current.setQueryParameters === 'function') {
          df.current.setQueryParameters({
            searchConfig: {
              filterSpecs: args.filter,
              boostSpecs: [],
            },
            // Add session parameters here
            parameters: sessionParams,
          });
        }
      } catch (error) {
        // Error setting session parameters
      }
    };

    updateSessionParams();

    const decodeRelayId = (id?: string | null | undefined) => {
      if (!id) return null;

      if (Number.isInteger(Number(id))) {
        return Number(id).valueOf();
      }

      try {
        const decoded = atob(id);
        const parts = decoded.split(':');
        return parts?.length === 2 ? Number(parts[1]).valueOf() : null;
      } catch (error) {
        return null;
      }
    };

    // Handle private content sessions
    if (needsNewSession()) {
      // Start new session for private content
      const sessionId = generateSessionId(props.pageType as string, currentId);
      chatbotInstance.setSessionId(sessionId);
      chatbotInstance.resetGreeting();

      // Clear messages and start fresh session
      df.current.startNewSession({ retainHistory: false });

      // Update state manager
      chatbotStateManager.updatePageContext(props.pageType as string, currentId || null, currentIsPrivate);
    }

    // Configure filters based on page type
    if (props.pageType === 'znglist') {
      args.title = props.znglist?.title || args.title;

      const nodes = props.znglist?.znglist?.znglyContents ?? [];
      const nodesWithId = nodes.map((node) => decodeRelayId(node?.id)).filter((id) => !!id) as number[];

      const filter = nodesWithId.map((id) => `post_id = ${id}`).join(' OR ');

      args.message =
        'How can I assist you with this Znglist. **Note**: you can ask me any question about the content on this page.';

      /* All znglists (private and public) include both the znglist content AND all public posts
       This allows the agent to answer questions about the znglist while also being able
       to reference general public content for broader questions
      */
      args.filter[0].filter = `post_status: ANY("publish") OR (${filter})`;
    } else if (props.pageType === 'hub') {
      args.title = props.hub?.title || args.title;

      // Get the ids from the hubPage node
      const ids = [] as number[];

      props?.hub?.connectionHubToHps?.edges?.forEach((edge) => {
        if (!edge?.node) return;

        const hubPageNode = edge?.node as NonNullable<NonNullable<Hub['connectionHubToHps']>['edges']>[number]['node'];
        hubPageNode.hubPage?.node?.connectionHpToCs?.edges?.forEach((edge) => {
          if (!edge?.node) return;
          const catalogNode = edge?.node as NonNullable<
            NonNullable<typeof hubPageNode.hubPage.node>['connectionHpToCs']
          >['edges'][number]['node'];

          const catalogId = decodeRelayId(catalogNode?.catalogId);
          if (catalogId) {
            ids.push(catalogId);
          }
        });
      });

      const uniqueIds = Array.from(new Set(ids)); // Remove duplicates
      const filter = uniqueIds.map((id) => `post_id = ${id}`).join(' OR ');

      if (getIsStatus(props?.hub?.status, [PostStatusEnum.Private, PostStatusEnum.Hidden])) {
        args.message = 'How can I assist you with this Hub. **Note**: you can only ask about the content on this page.';
        if (uniqueIds.length > 0) {
          args.filter[0].filter = `(${filter})`;
        } else {
          args.filter[0].filter = 'post_id = -1';
        }
      } else if (getIsStatus(props?.hub?.status, [PostStatusEnum.HiddenBS])) {
        // For HiddenBS hubs, include both specific and searchable content
        if (uniqueIds.length > 0) {
          args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s") OR (${filter})`;
        } else {
          args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s")`;
        }
      } else {
        if (uniqueIds.length > 0) {
          args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s") OR (${filter})`;
        } else {
          args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s")`;
        }
      }
    } else if (props.pageType === 'front_page') {
      args.title = 'the AI Assistant';
      args.message = 'How can I assist you today?';

      // Default filter for front page with hidden_b_s
      args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s")`;
    } else {
      // For all other page types, just update the filter
      if (df.current && typeof df.current.setQueryParameters === 'function') {
        df.current.setQueryParameters({
          searchConfig: {
            filterSpecs: args.filter,
            boostSpecs: [],
          },
        });
      }
      return;
    }
    // TODO: This code is commented out because starting a new session is currently not required. Uncomment if session management changes in the future.

    // Update query parameters with verification
    const filterConfig = {
      searchConfig: {
        filterSpecs: args.filter,
        boostSpecs: [],
      },
    };

    // Helper function to update query parameters
    const updateQueryParameters = (config: typeof filterConfig) => {
      if (df.current && typeof df.current.setQueryParameters === 'function') {
        df.current.setQueryParameters(config);
        // Store the filter config in chatbot instance to preserve it between parameter updates
        chatbotInstance.updateQueryParameters(config);
      }
    };

    // Apply filter immediately
    updateQueryParameters(filterConfig);

    // Add a small delay to ensure filter is applied (for race conditions)
    setTimeout(() => {
      updateQueryParameters(filterConfig);
    }, 100);

    // Only show greeting if we should
    if (shouldShowGreeting()) {
      // Wait for chat to be ready and then show greeting
      chatbotInstance.waitForChatReady().then(async (isReady) => {
        if (!isReady) {
          return;
        }

        // Double-check that greeting still hasn't been shown
        if (chatbotInstance.hasGreetingInDOM() || chatbotInstance.hasGreetingBeenShown()) {
          return;
        }

        const messenger = chatbotInstance.getDFMessenger() || df.current;
        if (!messenger) {
          return;
        }

        // Clear any existing greetings first
        chatbotInstance.clearExistingGreetings();

        // Alternative: Try starting a fresh session to clear everything
        if (messenger.startNewSession) {
          messenger.startNewSession({ retainHistory: false });
        }

        const greetingPayload = {
          greeting: {
            title: `Welcome to ${args.title}!`,
            message: args.message,
            // icon: 'https://example.com/icon.png', // Replace with your icon URL
          },
        } as CustomGreetingPayload;

        messenger.renderCustomCard([
          {
            type: 'custom_template',
            name: 'df-custom-greeting',
            payload: JSON.stringify(greetingPayload),
          },
        ]);

        // Mark greeting as shown
        chatbotInstance.markGreetingAsShown();
      });
    }

    // Update refs and state for next comparison
    initPageType.current = props.pageType;
    isPrivateContent.current = currentIsPrivate;
    initId.current = currentId;

    // Always update the current page context
    chatbotStateManager.updatePageContext(props.pageType, currentId || null, currentIsPrivate);
  }, [props.pageType, props.hub?.id, props.hub?.connectionHubToHps?.edges?.length, props.znglist?.id]);
};
